import { Vector2 } from '../math/Vector2';

/** The shapes a {@link Collider} can take. */
export const ColliderShape = {
  /** A circle of {@link Collider.radius}, the cheapest and the right default for round art. */
  Circle: 'circle',
  /** An axis aligned box of {@link Collider.size}. */
  Box: 'box',
} as const;

/** One of the {@link ColliderShape} values. */
export type ColliderShape = (typeof ColliderShape)[keyof typeof ColliderShape];

/**
 * Group every collider belongs to and collides with unless a game says otherwise.
 *
 * Groups are a bit mask so a pair test is two ands and no allocation. A game names its own bits in its
 * content data, which is where a decision like "cones are group 2" belongs.
 */
export const DEFAULT_COLLISION_GROUP = 1;

/** Mask that collides with every group. */
export const ALL_COLLISION_GROUPS = ~0;

const DEFAULT_RADIUS = 50;
const DEFAULT_SIZE = 100;

/** Field values a {@link Collider} can be built with. Omitted fields keep their default. */
export interface ColliderOptions {
  /** Which shape the collider takes. */
  readonly shape?: ColliderShape;
  /** Radius in world units, used by a circle. */
  readonly radius?: number;
  /** Size in world units, used by a box. */
  readonly size?: Vector2;
  /** Offset from the entity's position, in the entity's own space. */
  readonly offset?: Vector2;
  /** Bit mask of the groups this collider belongs to. */
  readonly group?: number;
  /** Bit mask of the groups this collider tests against. */
  readonly mask?: number;
  /** Whether the collider takes part in the broadphase at all. */
  readonly enabled?: boolean;
}

/**
 * Gives an entity a shape that overlaps other shapes.
 *
 * Pure data. Detecting the overlap is `CollisionSystem`'s job, because it needs every collider at once
 * rather than one entity's, and reacting to it is a behavior's, because that is a game rule.
 *
 * There is no physics behind this and there is not meant to be: colliders report that two things are
 * touching, and nothing here pushes anything apart or conserves momentum. Rotation is ignored, so a
 * box is always axis aligned; a rotating entity that needs an exact shape uses a circle.
 */
export class Collider {
  /** Which shape this collider takes. */
  shape: ColliderShape;
  /** Radius in world units before the entity's world scale, used when the shape is a circle. */
  radius: number;
  /** Size in world units before the entity's world scale, used when the shape is a box. */
  readonly size: Vector2;
  /** Offset from the entity's position, in the entity's own space before scale and rotation. */
  readonly offset: Vector2;
  /** Bit mask of the groups this collider belongs to. */
  group: number;
  /** Bit mask of the groups this collider tests against; a pair collides only if both masks agree. */
  mask: number;
  /** Whether the collider takes part in the broadphase. */
  enabled: boolean;

  constructor(options: ColliderOptions = {}) {
    this.shape = options.shape ?? ColliderShape.Circle;
    this.radius = options.radius ?? DEFAULT_RADIUS;
    this.size = options.size ?? new Vector2(DEFAULT_SIZE, DEFAULT_SIZE);
    this.offset = options.offset ?? Vector2.zero();
    this.group = options.group ?? DEFAULT_COLLISION_GROUP;
    this.mask = options.mask ?? ALL_COLLISION_GROUPS;
    this.enabled = options.enabled ?? true;
  }
}
